import { InventoryManagement20 } from '@carbon/icons-react';
import React from 'react';
import type { Product } from '#config/products';
import PageQuery from '#core/PageQuery';
import LegacyAppLayout from '#coreComponents/LegacyAppLayout';
import SupportMessageInProduct from '#coreComponents/SupportMessageInProduct/SupportMessageInProduct';
import type Products from '.';
import ImpactDetailsUpsell from './components/sdgImpactUpsell/ImpactDetailsUpsell';
import SdgUpsell from './detailsIframe/SdgUpsell/SdgUpsell';

const productPage = (productName: keyof typeof Products) => async (): Promise<{
  default: React.ComponentType<any>;
}> => {
  // this cannot be a param, dont try to make this function generic
  const products = (await import('.')).default;
  return {
    default: products[productName],
  };
};

// * * iframe pages * * //
const OverviewIframe = React.lazy(productPage('OverviewIframe'));
const DetailsIndexIframe = React.lazy(productPage('DetailsIndexIframe'));
const DetailsIframe = React.lazy(productPage('DetailsIframe'));

// * * Non-iframe pages * * //
const Overview = React.lazy(productPage('Overview'));
const DetailsIndex = React.lazy(productPage('DetailsIndex'));
const Details = React.lazy(productPage('Details'));

const sdgImpactConfig: Product = {
  name: 'SDG Impact',
  authKey: 'sdgImpact',
  description:
    'Access the powerful data behind THE’s Sustainability Impact Ratings, based on the UN’s Sustainable Development Goals',
  path: 'sdg',
  icon: InventoryManagement20,
  color: 'electricViolet',
  pageQuery: PageQuery,
  usesLegacyPermissions: true,
  featureFlagId: 'impact',
  disabledFeatureComponent: () => (
    <SupportMessageInProduct productName="SDG Impact Dashboard" />
  ),
  productConfig: {
    topbar: {
      title: 'SDG Impact',
      titleAcronym: 'SDG',
      rank: '1',
      filter: {
        hasPeerSelector: true,
        hasQueryBuilder: false,
      },
    },
  },
  pages: [
    //* * Non-iframe pages * * //
    {
      name: 'Overview',
      path: 'overview',
      component: Overview,
      pageConfig: {},
    },
    {
      name: 'Details',
      path: 'details',
      component: DetailsIndex,
      upsellComponent: ImpactDetailsUpsell,
      pageConfig: {},
    },
    {
      name: 'Details',
      path: 'details/:sdgId',
      component: Details,
      upsellComponent: ImpactDetailsUpsell,
      hiddenPage: true,
      pageConfig: {},
    },
    {
      name: 'Details',
      path: 'details/:sdgId/:year',
      component: Details,
      upsellComponent: ImpactDetailsUpsell,
      hiddenPage: true,
      pageConfig: {},
    },
    //* * iframe pages * * //
    {
      name: 'Overview',
      path: 'overview-impact',
      component: OverviewIframe,
      upsellComponent: SdgUpsell,
      layout: LegacyAppLayout,
      pageConfig: {},
    },
    {
      name: 'Details',
      path: 'details-impact',
      component: DetailsIndexIframe,
      upsellComponent: ImpactDetailsUpsell,
      layout: LegacyAppLayout,
      pageConfig: {},
    },
    {
      name: 'Details',
      path: 'details-impact/:sdgId',
      component: DetailsIframe,
      layout: LegacyAppLayout,
      upsellComponent: ImpactDetailsUpsell,
      hiddenPage: true,
      pageConfig: {},
    },
    {
      name: 'Details',
      path: 'details-impact/:sdgId/:year',
      component: DetailsIframe,
      layout: LegacyAppLayout,
      upsellComponent: ImpactDetailsUpsell,
      hiddenPage: true,
      pageConfig: {},
    },
  ],
};

export default sdgImpactConfig;
